const EXPERIENCE_NAME = "submit your project"
const PAGE_DESCRIPTION =
  "submit your project annsacks's customer support team for product support, questions and more. Find our number or chat with a representative."
const PAGE_KEYWORDS =
  "submit your project annsacks, submit your project annsacks customer support, submit your project annsacks support"
const SUB_CATEGORY = `annsacks:${EXPERIENCE_NAME}`
const PAGE_TITLE = "submit your project annsacks Customer Service"
const withFallback = (value, fallback) =>
  !value?.trim() || value.trim().toLowerCase() === "n/a" ? fallback : value

export const PROJECT_SUBMISSION_SECTIONS = {
  contactInformation: "contact information",
  projectInformation: "project information",
  attribution: "attribution",
  projectDocuments: "project documents",
}

const getPage = () => {
  const { adobeDataLayer: { getState } = {} } = window
  const statePage = (getState && getState("page")) || {}
  const pageObj = window.pageObj || {}
  const page = {
    ...pageObj,
    ...statePage,
    category: {
      ...pageObj.category,
      ...statePage.category,
    },
    pageInfo: {
      ...pageObj.pageInfo,
      ...statePage.pageInfo,
    },
    site: {
      ...pageObj.site,
      ...statePage.site,
    },
    user: {
      ...pageObj.user,
      ...statePage.user,
    },
  }

  return {
    ...page,
    category: {
      ...page.category,
      pageType: EXPERIENCE_NAME,
      primaryCategory: SUB_CATEGORY,
      subCategory1: SUB_CATEGORY,
      subCategory2: "n/a",
      subCategoryID: EXPERIENCE_NAME,
      subCategoryID2: "n/a",
      subCategoryName: EXPERIENCE_NAME,
    },
    pageInfo: {
      ...page.pageInfo,
      contentType: EXPERIENCE_NAME,
      description: PAGE_DESCRIPTION,
      keywords: PAGE_KEYWORDS,
      language: withFallback(page.pageInfo?.language, "en"),
      mktgCampaign: withFallback(page.pageInfo?.mktgCampaign, "n/a"),
      pageName: SUB_CATEGORY,
      redirect: withFallback(page.pageInfo?.redirect, "n/a"),
      title: PAGE_TITLE,
    },
  }
}

const getCurrentUrl = () => window.location?.href || "n/a"

const pushEvent = eventInfo => {
  window.adobeDataLayer = window.adobeDataLayer || []
  window.adobeDataLayer.push({
    event: "cmp:click",
    eventInfo,
    page: getPage(),
  })
}

const normalize = value =>
  String(value || "n/a")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .trim()
    .toLowerCase()

export const addPageViewAnalytics = () => {
  let page = getPage()
  const dataLayer = (window.adobeDataLayer = window.adobeDataLayer || [])

  if (!Object.keys(page.site || {}).length) {
    let pageReadyTimer
    const handlePageReady = () => {
      pageReadyTimer = window.setTimeout(addPageViewAnalytics)
    }

    window.addEventListener("datalayerTopFired", handlePageReady, {
      once: true,
    })
    return () => {
      window.removeEventListener("datalayerTopFired", handlePageReady)
      window.clearTimeout(pageReadyTimer)
    }
  }

  window.pageObj = page
  if (
    window.initDataLayerTop &&
    !window.datalayerTopLoaded &&
    page.category &&
    page.pageInfo &&
    page.site
  ) {
    window.initDataLayerTop()
    page = getPage()
  }

  const pageViewAlreadyAdded = dataLayer.some(
    item => item?.event === "cmp:show"
  )
  if (!pageViewAlreadyAdded) {
    dataLayer.push({
      event: "cmp:show",
      page,
    })
  }

  return undefined
}

export const addFormProgressAnalytics = (section, isFormStart = false) => {
  const action = isFormStart ? "form start" : "form interaction"
  const zone = `${EXPERIENCE_NAME}:${section}`

  pushEvent({
    clickInternalLinks: "true",
    eventAction: `${zone}:${action}`,
    eventMsg: "n/a",
    eventName: `${zone}:${action}`,
    eventStatus: "n/a",
    eventType: "navigation",
    internalLinkName: action,
    internalLinkPosition: section,
    internalLinkType: zone,
    internalLinkURL: getCurrentUrl(),
    internalLinkZoneName: zone,
  })
}

export const addUploadAnalytics = (status, message = "n/a") => {
  const section = PROJECT_SUBMISSION_SECTIONS.projectDocuments
  const action = `upload ${status}`
  const zone = `${EXPERIENCE_NAME}:${section}`

  pushEvent({
    clickInternalLinks: "true",
    eventAction: `${zone}:${action}`,
    eventMsg: normalize(message),
    eventName: `${zone}:${action}`,
    eventStatus: "n/a",
    eventType: "navigation",
    internalLinkName: action,
    internalLinkPosition:
      status === "initiated"
        ? zone
        : PROJECT_SUBMISSION_SECTIONS.projectDocuments,
    internalLinkType: zone,
    internalLinkURL: getCurrentUrl(),
    internalLinkZoneName: zone,
  })
}

export const addFieldErrorAnalytics = ({
  section,
  message,
  internalLinkName = "form field error",
}) => {
  const action = `${EXPERIENCE_NAME}:form input field errors`
  const zone = `${EXPERIENCE_NAME}:${section}`

  pushEvent({
    clickInternalLinks: "true",
    eventAction: action,
    eventMsg: normalize(message),
    eventName: action,
    eventStatus: "failure",
    eventType: "navigation",
    internalLinkName,
    internalLinkPosition: zone,
    internalLinkType: zone,
    internalLinkURL: getCurrentUrl(),
    internalLinkZoneName: zone,
  })
}

export const addConsentAnalytics = ({ accepted, details }) => {
  const state = accepted ? "accepted" : "removed"
  const action = `${EXPERIENCE_NAME}:consent checkbox ${state}`
  const type = `${EXPERIENCE_NAME}:consent ${
    accepted ? "selection" : "removed"
  }`

  pushEvent({
    clickInternalLinks: "true",
    eventAction: action,
    eventMsg: "n/a",
    eventName: action,
    eventStatus: "n/a",
    eventType: "navigation",
    internalLinkName: normalize(details),
    internalLinkPosition: EXPERIENCE_NAME,
    internalLinkType: type,
    internalLinkURL: getCurrentUrl(),
    internalLinkZoneName: type,
  })
}

export const addSubmitAnalytics = ({
  success,
  message,
  orderID,
  projectType,
  projectLocation,
}) => {
  const result = success ? "form success" : "form submit failure"
  const action = `${EXPERIENCE_NAME}:${result}`
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: action,
    eventMsg: normalize(message),
    eventName: action,
    eventStatus: success ? "success" : "failure",
    eventType: "navigation",
    internalLinkName: "submit",
    internalLinkPosition: EXPERIENCE_NAME,
    internalLinkType: action,
    internalLinkURL: getCurrentUrl(),
    internalLinkZoneName: action,
  }

  if (success) {
    if (orderID) eventInfo.orderID = normalize(orderID)
    eventInfo.projectType = normalize(projectType)
    eventInfo.projectLocation = normalize(projectLocation)
  }

  pushEvent(eventInfo)
}
